import { api } from './axios';

export type InquirySource = 'WALK_IN' | 'REFERRAL' | 'PHONE' | 'OTHER';
export type InquiryStatus = 'NEW' | 'CONTACTED' | 'VISIT_SCHEDULED' | 'ENROLLED' | 'DROPPED';
export type ComplaintPriority = 'LOW' | 'MEDIUM' | 'HIGH';
export type ComplaintStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
export type NoticeCategory = 'ACADEMIC' | 'FEE' | 'EVENT' | 'GENERAL';
export type NoticeTargetType = 'ALL' | 'STAFF_ONLY' | 'STUDENTS_ALL' | 'CLASS_SPECIFIC';

export interface DocumentType {
  id: number;
  name: string;
  code: string;
  active: boolean;
  issued: number;
}

export interface DocumentTypeInput {
  name: string;
  code: string;
}

export interface Visitor {
  id: number;
  visitorPassNumber: number;
  visitorName: string;
  cnic: string | null;
  purpose: string;
  meetingStaffId: number | null;
  meetingStaffName: string | null;
  authorizedByStaffId: number | null;
  authorizedByStaffName: string | null;
  checkInTime: string;
  checkOutTime: string | null;
}

export interface CheckInVisitorInput {
  visitorName: string;
  cnic?: string;
  purpose: string;
  meetingStaffId?: number | null;
  authorizedByStaffId?: number | null;
}

export interface Inquiry {
  id: number;
  studentName: string;
  classInterestedId: number | null;
  classInterestedName: string | null;
  guardianName: string | null;
  guardianPhone: string | null;
  source: InquirySource;
  followUpStatus: InquiryStatus;
  assignedToStaffId: number | null;
  assignedToStaffName: string | null;
  inquiryDate: string;
  remarks: string | null;
  convertedStudentId: number | null;
  convertedStudentName: string | null;
  convertedStudentRegistrationNumber: string | null;
  convertedAt: string | null;
  createdAt: string;
}

export interface InquiryInput {
  studentName: string;
  classInterestedId?: number | null;
  guardianName?: string;
  guardianPhone?: string;
  source: InquirySource;
  assignedToStaffId?: number | null;
  inquiryDate: string;
  remarks?: string;
}

export interface Complaint {
  id: number;
  category: string;
  subject: string;
  description: string;
  priority: ComplaintPriority;
  status: ComplaintStatus;
  assignedToStaffId: number | null;
  assignedToStaffName: string | null;
  resolutionNotes: string | null;
  resolvedAt: string | null;
  complainantName: string | null;
  complainantPhone: string | null;
  studentId: number | null;
  studentName: string | null;
  studentRegistrationNumber: string | null;
  createdAt: string;
}

export interface ComplaintInput {
  category: string;
  subject: string;
  description: string;
  priority: ComplaintPriority;
  complainantName?: string;
  complainantPhone?: string;
  studentId?: number | null;
  assignedToStaffId?: number | null;
}

export interface Issuance {
  id: number;
  documentTypeId: number;
  documentTypeName: string | null;
  documentTypeCode: string | null;
  documentNumber: number;
  documentRef: string;
  studentId: number;
  studentName: string | null;
  studentRegistrationNumber: string | null;
  issueDate: string;
  reissue: boolean;
  reissueReason: string | null;
  originalIssuanceId: number | null;
  originalDocumentNumber: number | null;
  issuedBy: number;
  createdAt: string;
}

export interface IssueInput {
  documentTypeId: number;
  studentId: number;
  issueDate: string;
}

export interface ReissueInput {
  originalIssuanceId: number;
  issueDate: string;
  reason: string;
}

export interface Notice {
  id: number;
  title: string;
  body: string;
  category: NoticeCategory;
  targetType: NoticeTargetType;
  targetClassId: number | null;
  targetClassName: string | null;
  effectiveDate: string;
  active: boolean;
  createdAt: string;
}

export interface NoticeInput {
  title: string;
  body: string;
  category: NoticeCategory;
  targetType: NoticeTargetType;
  targetClassId?: number | null;
  effectiveDate: string;
}

export const frontDeskApi = {
  // document types
  listDocumentTypes: (activeOnly = false) =>
    api.get<DocumentType[]>('/frontdesk/document-types', { params: { activeOnly } }).then((r) => r.data),
  createDocumentType: (body: DocumentTypeInput) =>
    api.post<DocumentType>('/frontdesk/document-types', body).then((r) => r.data),
  updateDocumentType: (id: number, body: DocumentTypeInput) =>
    api.put<DocumentType>(`/frontdesk/document-types/${id}`, body).then((r) => r.data),
  toggleDocumentType: (id: number, value: boolean) =>
    api
      .patch<DocumentType>(`/frontdesk/document-types/${id}/active`, null, { params: { value } })
      .then((r) => r.data),

  // visitors
  listVisitors: (from: string, to: string, openOnly = false) =>
    api.get<Visitor[]>('/frontdesk/visitors', { params: { from, to, openOnly } }).then((r) => r.data),
  checkInVisitor: (body: CheckInVisitorInput) =>
    api.post<Visitor>('/frontdesk/visitors/check-in', body).then((r) => r.data),
  checkOutVisitor: (id: number) =>
    api.post<Visitor>(`/frontdesk/visitors/${id}/check-out`).then((r) => r.data),

  // inquiries
  listInquiries: (params: {
    from: string;
    to: string;
    status?: InquiryStatus;
    source?: InquirySource;
    q?: string;
  }) => api.get<Inquiry[]>('/frontdesk/inquiries', { params }).then((r) => r.data),
  getInquiry: (id: number) => api.get<Inquiry>(`/frontdesk/inquiries/${id}`).then((r) => r.data),
  createInquiry: (body: InquiryInput) => api.post<Inquiry>('/frontdesk/inquiries', body).then((r) => r.data),
  updateInquiry: (id: number, body: InquiryInput) =>
    api.put<Inquiry>(`/frontdesk/inquiries/${id}`, body).then((r) => r.data),
  updateInquiryStatus: (id: number, body: { followUpStatus: InquiryStatus; remarks?: string }) =>
    api.patch<Inquiry>(`/frontdesk/inquiries/${id}/status`, body).then((r) => r.data),
  convertInquiry: (id: number, body: { studentId: number }) =>
    api.post<Inquiry>(`/frontdesk/inquiries/${id}/convert`, body).then((r) => r.data),
  deleteInquiry: (id: number) => api.delete<void>(`/frontdesk/inquiries/${id}`).then((r) => r.data),

  // complaints
  listComplaints: (params: {
    from: string;
    to: string;
    status?: ComplaintStatus;
    priority?: ComplaintPriority;
    q?: string;
  }) => api.get<Complaint[]>('/frontdesk/complaints', { params }).then((r) => r.data),
  getComplaint: (id: number) => api.get<Complaint>(`/frontdesk/complaints/${id}`).then((r) => r.data),
  createComplaint: (body: ComplaintInput) =>
    api.post<Complaint>('/frontdesk/complaints', body).then((r) => r.data),
  updateComplaint: (id: number, body: ComplaintInput) =>
    api.put<Complaint>(`/frontdesk/complaints/${id}`, body).then((r) => r.data),
  updateComplaintStatus: (
    id: number,
    body: { status: ComplaintStatus; assignedToStaffId?: number | null; resolutionNotes?: string },
  ) => api.patch<Complaint>(`/frontdesk/complaints/${id}/status`, body).then((r) => r.data),
  deleteComplaint: (id: number) =>
    api.delete<void>(`/frontdesk/complaints/${id}`).then((r) => r.data),

  // issuances
  issuancesByStudent: (studentId: number) =>
    api.get<Issuance[]>(`/frontdesk/issuances/by-student/${studentId}`).then((r) => r.data),
  issuancesInRange: (from: string, to: string) =>
    api.get<Issuance[]>('/frontdesk/issuances', { params: { from, to } }).then((r) => r.data),
  issueDocument: (body: IssueInput) =>
    api.post<Issuance>('/frontdesk/issuances', body).then((r) => r.data),
  reissueDocument: (body: ReissueInput) =>
    api.post<Issuance>('/frontdesk/issuances/reissue', body).then((r) => r.data),

  // notices
  listNotices: (params: {
    from: string;
    to: string;
    category?: NoticeCategory;
    targetType?: NoticeTargetType;
    activeOnly?: boolean;
  }) => api.get<Notice[]>('/frontdesk/notices', { params }).then((r) => r.data),
  getNotice: (id: number) => api.get<Notice>(`/frontdesk/notices/${id}`).then((r) => r.data),
  createNotice: (body: NoticeInput) => api.post<Notice>('/frontdesk/notices', body).then((r) => r.data),
  updateNotice: (id: number, body: NoticeInput) =>
    api.put<Notice>(`/frontdesk/notices/${id}`, body).then((r) => r.data),
  toggleNotice: (id: number, value: boolean) =>
    api.patch<Notice>(`/frontdesk/notices/${id}/active`, null, { params: { value } }).then((r) => r.data),
  deleteNotice: (id: number) => api.delete<void>(`/frontdesk/notices/${id}`).then((r) => r.data),
};
